/**
 * <pg-carousel>: enhances a native scroll-snap track (no library; replaces Swiper).
 *
 *   <pg-carousel class="pg-carousel" style="--pg-carousel-per-view: 4">
 *     <ul class="pg-carousel__track" ref="track">…</ul>
 *     <button ref="prev">…</button> <button ref="next">…</button>   (anywhere inside, optional)
 *     <div class="pg-carousel__progress" ref="progress"><span></span></div>   (optional)
 *   </pg-carousel>
 *
 * Arrows scroll by one page of items and disable at the ends; the progress bar shows the
 * visible fraction and position. Swipe, trackpad and keyboard scrolling work natively.
 * Never autoplays (DESIGN.md).
 */
import { PgElement, define, reducedMotion } from '@pg/core';

class PgCarousel extends PgElement {
  /** @type {number} */
  #frame = 0;

  mount() {
    const { track, prev, next } = this.refs;
    if (!track) return;
    prev?.addEventListener('click', () => this.#page(-1), { signal: this.signal });
    next?.addEventListener('click', () => this.#page(1), { signal: this.signal });
    track.addEventListener('scroll', () => this.#schedule(), { passive: true, signal: this.signal });
    new ResizeObserver(() => this.#schedule()).observe(track);
    this.#update();
  }

  #page(direction) {
    const { track } = this.refs;
    const item = /** @type {HTMLElement|null} */ (track.firstElementChild);
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = item ? item.offsetWidth + gap : track.clientWidth;
    const perPage = Math.max(1, Math.floor((track.clientWidth + gap) / step));
    track.scrollBy({ left: direction * perPage * step, behavior: reducedMotion() ? 'auto' : 'smooth' });
  }

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const { track, prev, next, progress } = this.refs;
    const max = track.scrollWidth - track.clientWidth;
    const atStart = track.scrollLeft <= 2;
    const atEnd = track.scrollLeft >= max - 2;
    if (prev) prev.disabled = atStart;
    if (next) next.disabled = atEnd;
    this.toggleAttribute('data-scrollable', max > 2);
    if (progress) {
      const visible = Math.min(1, track.clientWidth / track.scrollWidth);
      const position = max > 0 ? track.scrollLeft / max : 0;
      progress.style.setProperty('--pg-carousel-thumb', `${visible * 100}%`);
      progress.style.setProperty('--pg-carousel-offset', `${(position * (1 - visible) * 100) / visible}%`);
    }
  }
}

define('pg-carousel', PgCarousel);
